import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useRouter } from 'next/router'
import {
    Alert,
    Typography,
    Button,
    Collapse,
    Drawer,
    Empty,
    Modal,
    Spin,
    Table,
    Tabs,
    Tag,
    Tooltip,
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { marked } from 'marked'
import {
    AgentActivityJob,
    AgentContentJob,
    AgentContentJobAttempt,
    AgentContentJobMode,
    AgentContentJobStatus,
    AgentJobFieldDiff,
    AgentJobItemChangeType,
    AgentJobItemDiff,
    CourseWideEditSectionOutcome,
    CourseWideEditSectionResult,
    GetAgentJobDiffResponse,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { MagicTextBoxReadOnly } from 'magic-text-box-lexical/src/components/MagicTextBoxReadOnly/MagicTextBoxReadOnly'
import { getSignedUrlForS3Key } from '@/api/cb/s3'
import { magicTextBoxErrorHandler } from '@/components/ContentTreeNode/ContentTreeLeafNode/MagicTextBoxEditor/MagicTextBoxErrorHandler'
import { useAccessUniversalContentLink } from '@/hooks/useAccessUniversalContentLink'
import {
    mtbReadOnlyOptionsNoOp,
    practiceProblemLinkPropsNoOp,
} from '@/utils/mtbNoOps'
import {
    getAgentContentJob,
    getAgentJobDiff,
    revertAgentJobRun,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import { useSetRecoilState } from 'recoil'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { hashContentPath } from 'common/src/ContentPath'
import { useInterval } from '@/hooks/useInterval'
import styles from './AgentRunDetailsDrawer.module.less'
import { AgentRunDetailsDrawerProps } from './AgentRunDetailsDrawer.types'
import { AGENT_MODE_LABEL, getAgentModelLabel } from './agentActivityLabels'
import { MagicTextBoxReadOnlyStyleClass } from 'magic-text-box-lexical/src/atoms/options/options.types'

const POLL_INTERVAL_MS = 5_000

const isActive = (status: AgentContentJobStatus): boolean =>
    status === AgentContentJobStatus.queued ||
    status === AgentContentJobStatus.waitingForLock ||
    status === AgentContentJobStatus.running

const statusLabel = (status: AgentContentJobStatus): string =>
    status === AgentContentJobStatus.canceled
        ? 'Stopped'
        : status === AgentContentJobStatus.waitingForLock
          ? 'Waiting for lock'
          : status

const statusColor = (status: AgentContentJobStatus): string | undefined => {
    if (status === AgentContentJobStatus.succeeded) return 'green'
    if (status === AgentContentJobStatus.failed) return 'red'
    if (status === AgentContentJobStatus.running) return 'blue'
    if (status === AgentContentJobStatus.waitingForLock) return 'gold'
    if (status === AgentContentJobStatus.canceled) return 'default'
    return undefined
}

const formatTime = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : '—'

const formatFileSize = (bytes: number): string =>
    bytes < 1024
        ? `${bytes} B`
        : bytes < 1024 * 1024
          ? `${(bytes / 1024).toFixed(1)} KB`
          : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

// Token totals run to the hundreds of millions (cache reads dominate), so the
// raw number is unreadable in a table cell.
const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`
    if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}k`
    return String(tokens)
}

const formatDuration = (startedAt: string, endedAt: string | null): string => {
    if (!endedAt) return '—'
    const seconds = Math.max(
        0,
        Math.floor(
            (new Date(endedAt).getTime() - new Date(startedAt).getTime()) /
                1_000
        )
    )
    const minutes = Math.floor(seconds / 60)
    return minutes >= 60
        ? `${Math.floor(minutes / 60)}h ${minutes % 60}m ${seconds % 60}s`
        : `${minutes}m ${seconds % 60}s`
}

// Live ticker for active runs: agents legitimately go quiet for minutes on
// long tool calls, and the reader shouldn't have to diff the transcript's
// last [Xm Ys] stamp against a clock in their head. updatedAt bumps on every
// progress append, so now - updatedAt IS "time since last update".
const LiveRunTicker: React.FC<{
    startedAt: string | null
    createdAt: string
    updatedAt: string
}> = ({ startedAt, createdAt, updatedAt }): ReactElement => {
    const [nowMs, setNowMs] = useState<number>(() => Date.now())
    useInterval(() => setNowMs(Date.now()), 1_000)
    const runningMs = nowMs - new Date(startedAt ?? createdAt).getTime()
    const sinceUpdateSeconds = Math.max(
        0,
        Math.floor((nowMs - new Date(updatedAt).getTime()) / 1_000)
    )
    const minutes = Math.floor(Math.max(0, runningMs) / 60_000)
    const seconds = Math.floor((Math.max(0, runningMs) % 60_000) / 1_000)
    const sinceLabel =
        sinceUpdateSeconds >= 60
            ? `${Math.floor(sinceUpdateSeconds / 60)}m ${sinceUpdateSeconds % 60}s`
            : `${sinceUpdateSeconds}s`
    // The inactivity watchdog kills a silent CLI at 10 minutes — color the
    // staleness as it approaches that
    const tone =
        sinceUpdateSeconds >= 360
            ? 'danger'
            : sinceUpdateSeconds >= 120
              ? 'warning'
              : 'secondary'
    return (
        <div className={styles.liveTicker}>
            <Tag color={'blue'}>
                running {minutes >= 60 ? `${Math.floor(minutes / 60)}h ` : ''}
                {minutes % 60}m {seconds}s
            </Tag>
            <Typography.Text type={tone as never}>
                last update {sinceLabel} ago
            </Typography.Text>
        </div>
    )
}

const changeTagColor: Record<AgentJobItemChangeType, string> = {
    [AgentJobItemChangeType.added]: 'green',
    [AgentJobItemChangeType.removed]: 'red',
    [AgentJobItemChangeType.modified]: 'blue',
}

const DiffFieldContent: React.FC<{
    value: string
    isRichText: boolean
    accessUniversalContentLink: ReturnType<typeof useAccessUniversalContentLink>
}> = ({ value, isRichText, accessUniversalContentLink }): ReactElement =>
    isRichText ? (
        <MagicTextBoxReadOnly
            initialEditorState={value}
            namespace={'agentRunDiff'}
            getPreSignedUrlForS3Key={getSignedUrlForS3Key}
            onError={(error: Error) =>
                magicTextBoxErrorHandler(error, 'agent run diff')
            }
            practiceProblemLinkProps={practiceProblemLinkPropsNoOp}
            options={{
                styleClassName: MagicTextBoxReadOnlyStyleClass.DarkMode,
            }}
            accessUniversalContentLink={accessUniversalContentLink}
        />
    ) : (
        <pre className={styles.plainFieldText}>{value}</pre>
    )

const DiffFieldView: React.FC<{
    field: AgentJobFieldDiff
    accessUniversalContentLink: ReturnType<typeof useAccessUniversalContentLink>
}> = ({ field, accessUniversalContentLink }): ReactElement => {
    const sides = [
        {
            caption: 'Before',
            value: field.before,
            className: styles.beforePane,
        },
        { caption: 'After', value: field.after, className: styles.afterPane },
    ].filter((side) => side.value !== null)
    return (
        <div className={styles.fieldDiff}>
            <div className={styles.fieldLabel}>{field.label}</div>
            <div
                className={
                    sides.length === 2
                        ? styles.beforeAfterGrid
                        : styles.singlePane
                }
            >
                {sides.map((side) => (
                    <div
                        className={`${styles.pane} ${side.className}`}
                        key={side.caption}
                    >
                        {field.before !== null && field.after !== null && (
                            <div className={styles.paneCaption}>
                                {side.caption}
                            </div>
                        )}
                        <DiffFieldContent
                            value={side.value as string}
                            isRichText={field.isRichText}
                            accessUniversalContentLink={
                                accessUniversalContentLink
                            }
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}

const renderDiffFields = (
    item: AgentJobItemDiff,
    accessUniversalContentLink: ReturnType<typeof useAccessUniversalContentLink>
): ReactElement => (
    <div className={styles.diffFields}>
        {item.fields.map((field) => (
            <DiffFieldView
                field={field}
                accessUniversalContentLink={accessUniversalContentLink}
                key={field.label}
            />
        ))}
    </div>
)

const changeCountSummary = (items: AgentJobItemDiff[]): ReactElement => (
    <span className={styles.groupCounts}>
        {(
            [
                AgentJobItemChangeType.added,
                AgentJobItemChangeType.modified,
                AgentJobItemChangeType.removed,
            ] as const
        ).map((changeType) => {
            const count = items.filter(
                (item) => item.changeType === changeType
            ).length
            return count ? (
                <Tag color={changeTagColor[changeType]} key={changeType}>
                    {count} {changeType}
                </Tag>
            ) : null
        })}
    </span>
)

// Inner list of changed items for one content group. Panels must be DIRECT
// Collapse children: wrapped in an intermediate component antd never wires
// their toggles, and the items-prop API needs antd >= 5.6 (this repo pins 5.3)
const renderGroupItems = (
    items: AgentJobItemDiff[],
    accessUniversalContentLink: ReturnType<
        typeof useAccessUniversalContentLink
    >,
    buildOpenItem?: (item: AgentJobItemDiff) => (() => void) | null
): ReactElement => (
    <Collapse className={styles.itemCollapse}>
        {items.map((item) => {
            const openItem = buildOpenItem?.(item) ?? null
            return (
                <Collapse.Panel
                    key={item.id}
                    header={
                        <span className={styles.diffHeader}>
                            <Tag color={changeTagColor[item.changeType]}>
                                {item.changeType}
                            </Tag>
                            {item.title}
                            {openItem && (
                                <Button
                                    className={styles.openItemButton}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        openItem()
                                    }}
                                    size={'small'}
                                    type={'link'}
                                >
                                    Open
                                </Button>
                            )}
                        </span>
                    }
                >
                    {renderDiffFields(item, accessUniversalContentLink)}
                </Collapse.Panel>
            )
        })}
    </Collapse>
)

export const AgentRunDetailsDrawer: React.FC<AgentRunDetailsDrawerProps> = (
    props
): ReactElement => {
    const router = useRouter()
    const accessUniversalContentLink = useAccessUniversalContentLink()
    const [job, setJob] = useState<AgentContentJob | null>(null)
    // Server-resolved display names from getJob — the fallback when the drawer
    // opens from a cold deep link and no table row's enriched metadata exists
    const [display, setDisplay] = useState<{
        prettyCourseName?: string
        contentPathTitles?: string[]
    } | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [activeTab, setActiveTab] = useState<string>('overview')
    const [diff, setDiff] = useState<GetAgentJobDiffResponse | null>(null)
    const [diffLoading, setDiffLoading] = useState<boolean>(false)
    // Course-wide Changes tab: which section panels are open (null = all)
    const [sectionActiveKeys, setSectionActiveKeys] = useState<string[] | null>(
        null
    )
    const progressRef = useRef<HTMLDivElement | null>(null)
    const progressPinnedToBottom = useRef<boolean>(true)

    const refreshJob = useCallback(async (): Promise<void> => {
        if (!props.jobId) return
        const response = await getAgentContentJob({ jobId: props.jobId })
        if (response.data.success) {
            setJob(response.data.payload.job)
            setDisplay({
                prettyCourseName: response.data.payload.prettyCourseName,
                contentPathTitles: response.data.payload.contentPathTitles,
            })
        }
    }, [props.jobId])

    useEffect(() => {
        setJob(null)
        setDisplay(null)
        setDiff(null)
        setSectionActiveKeys(null)
        // The drawer tab rides the URL as a shallow `runTab` param, so the
        // browser Back button restores the tab the user left from (e.g.
        // returning from a Changes-item deep link lands on Changes again)
        setActiveTab(router.query.runTab === 'changes' ? 'changes' : 'overview')
        progressPinnedToBottom.current = true
        if (!props.jobId) return
        const jobId = props.jobId
        let isMounted = true
        setLoading(true)
        void (async (): Promise<void> => {
            try {
                const response = await getAgentContentJob({ jobId })
                if (!isMounted) return
                if (response.data.success) {
                    setJob(response.data.payload.job)
                    setDisplay({
                        prettyCourseName:
                            response.data.payload.prettyCourseName,
                        contentPathTitles:
                            response.data.payload.contentPathTitles,
                    })
                } else {
                    syncErrorMessage('Unable to load agent run details.')
                }
            } finally {
                if (isMounted) setLoading(false)
            }
        })()
        return () => {
            isMounted = false
        }
    }, [props.jobId])

    useEffect(() => {
        if (!job || !isActive(job.status)) return
        const interval = window.setInterval(
            (): void => void refreshJob(),
            POLL_INTERVAL_MS
        )
        return () => window.clearInterval(interval)
    }, [job, refreshJob])

    useEffect(() => {
        const element = progressRef.current
        if (element && progressPinnedToBottom.current) {
            element.scrollTop = element.scrollHeight
        }
    }, [job?.progressMessages])

    const loadDiff = useCallback(async (): Promise<void> => {
        if (!props.jobId || diff || diffLoading) return
        setDiffLoading(true)
        try {
            const response = await getAgentJobDiff({ jobId: props.jobId })
            if (response.data.success) {
                setDiff(response.data.payload)
            } else {
                syncErrorMessage('Unable to load content changes.')
            }
        } finally {
            setDiffLoading(false)
        }
    }, [diff, diffLoading, props.jobId])

    // Fires when the Changes tab is RESTORED from the URL (Back button)
    // rather than clicked; loadDiff self-guards against duplicate fetches
    useEffect(() => {
        if (activeTab === 'changes') void loadDiff()
    }, [activeTab, loadDiff])

    const attemptsColumns = useMemo<ColumnsType<AgentContentJobAttempt>>(
        () => [
            {
                title: 'Attempt',
                render: (_, __, index) => index + 1,
            },
            {
                title: 'Account',
                render: (_, attempt) => attempt.accountName ?? '—',
            },
            {
                title: 'Peak RSS MB',
                render: (_, attempt) => attempt.peakRssMb ?? '—',
            },
            {
                title: 'Cost',
                render: (_, attempt) =>
                    attempt.totalCostUsd === null
                        ? '—'
                        : `$${attempt.totalCostUsd.toFixed(2)}`,
            },
            {
                title: 'Tokens (in/out)',
                render: (_, attempt) =>
                    attempt.usage
                        ? `${formatTokenCount(attempt.usage.inputTokens + attempt.usage.cacheCreationInputTokens + attempt.usage.cacheReadInputTokens)} / ${formatTokenCount(attempt.usage.outputTokens)}`
                        : '—',
            },
            {
                title: 'Duration',
                render: (_, attempt) =>
                    formatDuration(attempt.startedAt, attempt.endedAt),
            },
            {
                title: 'Outcome',
                render: (_, attempt) => attempt.outcome ?? 'running',
            },
        ],
        []
    )

    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [isRerunning, setIsRerunning] = useState<boolean>(false)
    // Re-request a terminal-failed run: a NEW job with the same parameters
    // (jobs are never resumed). The server's lock/conflict checks still apply
    const rerunJob = useCallback(async (): Promise<void> => {
        if (!job) return
        setIsRerunning(true)
        try {
            const response = await startAgentContentJob({
                courseName: job.courseName,
                contentPath: job.contentPath,
                mode: job.mode,
                modelName: job.modelName,
                instructorRequest: job.instructorRequest ?? undefined,
                // Job records default this to [] on every mode; only forward
                // a real target list (the server rejects the field outside
                // counterpart-backfill mode)
                targetNotecardIds: job.targetNotecardIds?.length
                    ? job.targetNotecardIds
                    : undefined,
                sourceMaterialImport: job.sourceMaterialImport
                    ? {
                          sourceUrls: job.sourceMaterialImport.sourceUrls,
                          importMode: job.sourceMaterialImport.importMode,
                      }
                    : undefined,
                // previousExamAuthoring reruns are always valid — the four
                // exam files are stored server-side, keyed by this identifier
                previousExamAuthoring: job.previousExamAuthoring ?? undefined,
            })
            if (!response.data.success) {
                syncErrorMessage(
                    `Failed to rerun agent job: ${response.data.error}`
                )
                return
            }
            syncSuccessMessage('Agent job restarted with the same request.')
            setViewingState((current) => ({
                ...current,
                id: response.data.payload.jobId,
            }))
        } finally {
            setIsRerunning(false)
        }
    }, [job, setViewingState])

    const [isReverting, setIsReverting] = useState<boolean>(false)
    const revertRun = useCallback(async (): Promise<void> => {
        if (!job) return
        setIsReverting(true)
        try {
            const response = await revertAgentJobRun({ jobId: job.id })
            if (!response.data.success) {
                syncErrorMessage(`Failed to revert run: ${response.data.error}`)
                return
            }
            syncSuccessMessage(
                `Reverted the run — ${response.data.payload.sectionsReverted} ${
                    response.data.payload.sectionsReverted === 1
                        ? 'section'
                        : 'sections'
                } restored to their pre-run checkpoints.`
            )
            await refreshJob()
        } finally {
            setIsReverting(false)
        }
    }, [job, refreshJob])
    const confirmRevertRun = useCallback((): void => {
        const data = job?.courseWideEdit
        if (!data) return
        Modal.confirm({
            title: 'Revert this entire course-wide run?',
            content: `Restores all ${data.sectionsApplied} applied ${
                data.sectionsApplied === 1 ? 'section' : 'sections'
            } to their pre-run checkpoints as a unit. Safety checkpoints of the current state are created first, so the revert itself is recoverable.`,
            okText: 'Revert entire run',
            okButtonProps: { danger: true },
            onOk: async () => {
                await revertRun()
            },
        })
    }, [job, revertRun])

    const sectionResultColumns = useMemo<
        ColumnsType<CourseWideEditSectionResult>
    >(
        () => [
            {
                title: 'Section',
                render: (_, result) =>
                    result.contentPathTitles.length
                        ? result.contentPathTitles.join(' › ')
                        : result.hashedContentPath,
            },
            {
                title: 'Outcome',
                render: (_, result) => (
                    <Tag
                        color={
                            result.outcome ===
                            CourseWideEditSectionOutcome.applied
                                ? 'green'
                                : 'red'
                        }
                    >
                        {result.outcome}
                    </Tag>
                ),
            },
            {
                title: 'Error',
                render: (_, result) => result.error ?? '—',
            },
        ],
        []
    )

    const firstAttempt = job?.attempts[0]
    const lastAttempt = job?.attempts[job.attempts.length - 1]
    const metadata = props.jobMetadata
    const navigationJob: AgentActivityJob | null = job
        ? {
              jobId: job.id,
              courseName: job.courseName,
              prettyCourseName:
                  metadata?.prettyCourseName ??
                  display?.prettyCourseName ??
                  job.courseName,
              contentPath: job.contentPath,
              contentPathTitles:
                  metadata?.contentPathTitles ??
                  display?.contentPathTitles ??
                  job.contentPath.map((part) => String(part)),
              status: job.status,
              mode: job.mode,
              modelName: job.modelName,
              priorityClass: job.priorityClass,
              requestedByDisplayName: job.requestedByDisplayName,
              instructorRequest: job.instructorRequest ?? null,
              createdAt: job.createdAt,
              startedAt: firstAttempt?.startedAt ?? null,
              waitingOn: metadata?.waitingOn ?? null,
              targetNotecardIds:
                  metadata?.targetNotecardIds ?? job.targetNotecardIds ?? null,
          }
        : null

    const counts = job?.summary?.counts
    const countsLine = counts
        ? `Notecards: +${counts.notecardsAdded} / ~${counts.notecardsReplaced} / -${counts.notecardsDeleted} · Practice problems: +${counts.practiceProblemsAdded} / ~${counts.practiceProblemsReplaced} / -${counts.practiceProblemsDeleted}${(counts.counterpartsAdded ?? 0) || (counts.counterpartsReplaced ?? 0) || (counts.counterpartsDeleted ?? 0) ? ` · Counterparts: +${counts.counterpartsAdded ?? 0} / ~${counts.counterpartsReplaced ?? 0} / -${counts.counterpartsDeleted ?? 0}` : ''}${counts.formulaEntriesAdded || counts.formulaEntriesReplaced || counts.formulaEntriesDeleted ? ` · Formula entries: +${counts.formulaEntriesAdded} / ~${counts.formulaEntriesReplaced} / -${counts.formulaEntriesDeleted}` : ''}${counts.sourceMaterialsAdded || counts.sourceMaterialsReplaced || counts.sourceMaterialsDeleted ? ` · Source materials: +${counts.sourceMaterialsAdded} / ~${counts.sourceMaterialsReplaced} / -${counts.sourceMaterialsDeleted}` : ''}${counts.outlineChanged ? ' · outline updated' : ''}${counts.summaryChanged ? ' · summary updated' : ''}`
        : null

    const overview = job ? (
        <>
            {job.status === AgentContentJobStatus.waitingForLock &&
                metadata?.waitingOn && (
                    <Alert
                        type={'warning'}
                        showIcon
                        message={
                            <span>
                                Waiting for another run —{' '}
                                <Button
                                    type={'link'}
                                    size={'small'}
                                    onClick={() =>
                                        setViewingState((current) => ({
                                            ...current,
                                            id: metadata.waitingOn!.jobId,
                                        }))
                                    }
                                >
                                    {AGENT_MODE_LABEL[metadata.waitingOn.mode]}{' '}
                                    on{' '}
                                    {metadata.waitingOn.contentPathTitles.join(
                                        ' › '
                                    )}
                                </Button>
                            </span>
                        }
                    />
                )}
            <div className={styles.overviewGrid}>
                <span className={styles.overviewLabel}>Started by</span>
                <span>{job.requestedByDisplayName ?? 'unknown'}</span>
                <span className={styles.overviewLabel}>Mode</span>
                <span>{AGENT_MODE_LABEL[job.mode]}</span>
                {job.previousExamAuthoring && (
                    <>
                        <span className={styles.overviewLabel}>Exam</span>
                        <span>
                            {job.previousExamAuthoring.examIdentifier.year} ·{' '}
                            {
                                job.previousExamAuthoring.examIdentifier
                                    .sittingSeason
                            }{' '}
                            ·{' '}
                            {
                                job.previousExamAuthoring.examIdentifier
                                    .originalExamAbbreviation
                            }
                        </span>
                    </>
                )}
                <span className={styles.overviewLabel}>Model</span>
                <span>{getAgentModelLabel(job.modelName)}</span>
                {job.instructorRequest && (
                    <>
                        <span className={styles.overviewLabel}>Request</span>
                        <span className={styles.instructorRequestText}>
                            {job.instructorRequest}
                        </span>
                    </>
                )}
                <span className={styles.overviewLabel}>Created</span>
                <span>{formatTime(job.createdAt)}</span>
                <span className={styles.overviewLabel}>Started</span>
                <span>{formatTime(firstAttempt?.startedAt)}</span>
                <span className={styles.overviewLabel}>Ended</span>
                <span>{formatTime(lastAttempt?.endedAt)}</span>
            </div>
            {job.sourceMaterialImport && (
                <div>
                    <div className={styles.sectionHeading}>Import input</div>
                    <div>Mode: {job.sourceMaterialImport.importMode}</div>
                    {!!job.sourceMaterialImport.files.length && (
                        <ul>
                            {job.sourceMaterialImport.files.map((file) => (
                                <li key={`${file.name}-${file.sizeBytes}`}>
                                    {file.name} ·{' '}
                                    {formatFileSize(file.sizeBytes)}
                                </li>
                            ))}
                        </ul>
                    )}
                    {!!job.sourceMaterialImport.sourceUrls.length && (
                        <ul>
                            {job.sourceMaterialImport.sourceUrls.map((url) => (
                                <li key={url}>{url}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {job.mode === AgentContentJobMode.courseWideEdit &&
                job.courseWideEdit && (
                    <div>
                        <div className={styles.sectionHeading}>
                            Course-wide run
                        </div>
                        <div>
                            {job.courseWideEdit.sectionsApplied} of{' '}
                            {job.courseWideEdit.sectionsTouched} touched{' '}
                            {job.courseWideEdit.sectionsTouched === 1
                                ? 'section'
                                : 'sections'}{' '}
                            applied ({job.courseWideEdit.sectionsPlanned} in the
                            course){' '}
                            {job.courseWideEdit.capStopped && (
                                <Tag color={'orange'}>stopped at cap</Tag>
                            )}
                            {job.courseWideEdit.partial && (
                                <Tag color={'gold'}>partial</Tag>
                            )}
                            {job.courseWideEdit.revertedAt && (
                                <Tag color={'red'}>reverted</Tag>
                            )}
                        </div>
                        {job.courseWideEdit.revertedAt && (
                            <Alert
                                message={`This run was reverted ${formatTime(
                                    job.courseWideEdit.revertedAt
                                )} — every applied section was restored to its pre-run checkpoint.`}
                                showIcon
                                type={'info'}
                            />
                        )}
                        {!!job.courseWideEdit.sectionResults.length && (
                            <Table<CourseWideEditSectionResult>
                                columns={sectionResultColumns}
                                dataSource={job.courseWideEdit.sectionResults}
                                pagination={false}
                                rowKey={(result) => result.hashedContentPath}
                                scroll={{ x: true }}
                                size={'small'}
                            />
                        )}
                    </div>
                )}
            {job.status === AgentContentJobStatus.running && (
                <LiveRunTicker
                    startedAt={
                        job.attempts[job.attempts.length - 1]?.startedAt ?? null
                    }
                    createdAt={job.createdAt}
                    updatedAt={job.updatedAt}
                />
            )}
            {job.status !== AgentContentJobStatus.waitingForLock &&
                isActive(job.status) && (
                    <div
                        className={styles.progressLog}
                        ref={progressRef}
                        onScroll={(event) => {
                            const element = event.currentTarget
                            progressPinnedToBottom.current =
                                element.scrollHeight -
                                    element.scrollTop -
                                    element.clientHeight <
                                24
                        }}
                    >
                        {job.progressMessages
                            .slice(-100)
                            .map((message, index) => (
                                <div
                                    className={styles.progressLine}
                                    key={index}
                                >
                                    {message}
                                </div>
                            ))}
                    </div>
                )}
            {job.attempts.length > 0 && (
                <>
                    <div className={styles.sectionHeading}>Attempts</div>
                    <Table<AgentContentJobAttempt>
                        columns={attemptsColumns}
                        dataSource={job.attempts}
                        pagination={false}
                        rowKey={(_, index) => String(index)}
                        scroll={{ x: true }}
                        size={'small'}
                    />
                </>
            )}
            {(job.status === AgentContentJobStatus.failed ||
                job.status === AgentContentJobStatus.orphaned) && (
                <Alert
                    description={job.error}
                    message={`Agent job ${job.status}`}
                    showIcon
                    type={'error'}
                />
            )}
            {job.status === AgentContentJobStatus.succeeded && job.summary && (
                <Alert
                    description={
                        <>
                            <div
                                className={styles.changeSummary}
                                dangerouslySetInnerHTML={{
                                    __html: marked(
                                        job.summary.changeSummaryMarkdown,
                                        {}
                                    ),
                                }}
                            />
                            {countsLine && (
                                <div className={styles.counts}>
                                    {countsLine}
                                </div>
                            )}
                        </>
                    }
                    message={'Agent run complete'}
                    showIcon
                    type={'success'}
                />
            )}
        </>
    ) : null

    // Deep links from diff items into the content editors — same course only
    // (the mounted tree belongs to the displayed course), and removed items
    // have nothing left to open. Navigation pushes a history entry, so the
    // browser Back button returns to this drawer
    const canDeepLinkContent =
        !!job && job.courseName === props.displayedCourseName
    const buildOpenItemForGroup = useCallback(
        (sectionHash: string | null, groupKey: string) =>
            (item: AgentJobItemDiff): (() => void) | null => {
                if (!canDeepLinkContent || !sectionHash) return null
                if (item.changeType === AgentJobItemChangeType.removed)
                    return null
                const target =
                    groupKey === 'notecards'
                        ? {
                              viewingStateType: ViewingStateType.notecards,
                              id: item.id,
                          }
                        : groupKey === 'practiceProblems'
                          ? {
                                viewingStateType:
                                    ViewingStateType.practiceProblems,
                                id: item.id,
                            }
                          : groupKey === 'sourceMaterials'
                            ? {
                                  viewingStateType:
                                      ViewingStateType.sourceMaterial,
                                  id: item.id,
                              }
                            : groupKey === 'outlineSummary'
                              ? {
                                    viewingStateType:
                                        item.id === 'outline'
                                            ? ViewingStateType.outline
                                            : ViewingStateType.summary,
                                    id: null,
                                }
                              : null
                if (!target) return null
                return () =>
                    props.onOpenContentItem({
                        hashedContentPath: sectionHash,
                        ...target,
                    })
            },
        [canDeepLinkContent, props]
    )

    const changes = diffLoading ? (
        <Spin />
    ) : diff && !diff.available ? (
        <Alert
            message={'No content changes were applied by this run.'}
            showIcon
            type={'info'}
        />
    ) : diff ? (
        (() => {
            const buildGroups = (source: {
                outline: AgentJobItemDiff | null
                summary: AgentJobItemDiff | null
                notecards: AgentJobItemDiff[]
                practiceProblems: AgentJobItemDiff[]
                formulaEntries?: AgentJobItemDiff[]
                sourceMaterials?: AgentJobItemDiff[]
            }) =>
                [
                    {
                        key: 'outlineSummary',
                        title: 'Outline & summary',
                        items: [source.outline, source.summary].filter(
                            (item): item is AgentJobItemDiff => item !== null
                        ),
                    },
                    {
                        key: 'notecards',
                        title: 'Notecards',
                        items: source.notecards,
                    },
                    {
                        key: 'practiceProblems',
                        title: 'Practice problems',
                        items: source.practiceProblems,
                    },
                    {
                        key: 'formulaEntries',
                        title: 'Formula entries',
                        items: source.formulaEntries ?? [],
                    },
                    {
                        key: 'sourceMaterials',
                        title: 'Source materials',
                        items: source.sourceMaterials ?? [],
                    },
                ].filter((group) => group.items.length)
            const renderGroups = (
                groups: ReturnType<typeof buildGroups>,
                keyPrefix: string,
                sectionHash: string | null
            ): ReactElement => (
                <Collapse
                    className={styles.groupCollapse}
                    defaultActiveKey={groups.map(
                        (group) => keyPrefix + group.key
                    )}
                >
                    {groups.map((group) => (
                        <Collapse.Panel
                            key={keyPrefix + group.key}
                            header={
                                <span className={styles.groupHeader}>
                                    <span className={styles.groupTitle}>
                                        {group.title}
                                    </span>
                                    {changeCountSummary(group.items)}
                                </span>
                            }
                        >
                            {renderGroupItems(
                                group.items,
                                accessUniversalContentLink,
                                buildOpenItemForGroup(sectionHash, group.key)
                            )}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            )
            // courseWideEdit runs: one collapsible block per applied
            // section, with expand/collapse-all controls
            if (diff.sections?.length) {
                const allSectionKeys = diff.sections.map(
                    (section) => section.hashedContentPath
                )
                return (
                    <>
                        <div className={styles.changesToolbar}>
                            <Button
                                onClick={() =>
                                    setSectionActiveKeys(allSectionKeys)
                                }
                                size={'small'}
                            >
                                Expand all
                            </Button>
                            <Button
                                onClick={() => setSectionActiveKeys([])}
                                size={'small'}
                            >
                                Collapse all
                            </Button>
                        </div>
                        <Collapse
                            activeKey={sectionActiveKeys ?? allSectionKeys}
                            className={styles.groupCollapse}
                            onChange={(keys) =>
                                setSectionActiveKeys(
                                    Array.isArray(keys) ? keys : [keys]
                                )
                            }
                        >
                            {diff.sections.map((section) => {
                                const sectionGroups = buildGroups(section)
                                return (
                                    <Collapse.Panel
                                        header={
                                            <span
                                                className={styles.groupHeader}
                                            >
                                                <span
                                                    className={
                                                        styles.groupTitle
                                                    }
                                                >
                                                    {section.contentPathTitles.join(
                                                        ' › '
                                                    ) ||
                                                        section.hashedContentPath}
                                                </span>
                                                {changeCountSummary(
                                                    sectionGroups.flatMap(
                                                        (group) => group.items
                                                    )
                                                )}
                                            </span>
                                        }
                                        key={section.hashedContentPath}
                                    >
                                        {sectionGroups.length ? (
                                            renderGroups(
                                                sectionGroups,
                                                section.hashedContentPath,
                                                section.hashedContentPath
                                            )
                                        ) : (
                                            <Typography.Text type={'secondary'}>
                                                Rewritten, but textually
                                                identical to the previous
                                                version — nothing visibly
                                                changed in this section.
                                            </Typography.Text>
                                        )}
                                    </Collapse.Panel>
                                )
                            })}
                        </Collapse>
                    </>
                )
            }
            const groups = buildGroups(diff)
            return groups.length ? (
                renderGroups(
                    groups,
                    '',
                    job ? hashContentPath(job.contentPath) : null
                )
            ) : (
                <Empty
                    description={
                        counts &&
                        counts.notecardsAdded +
                            counts.notecardsReplaced +
                            counts.notecardsDeleted +
                            counts.practiceProblemsAdded +
                            counts.practiceProblemsReplaced +
                            counts.practiceProblemsDeleted +
                            (counts.counterpartsAdded ?? 0) +
                            (counts.counterpartsReplaced ?? 0) +
                            (counts.counterpartsDeleted ?? 0) +
                            counts.formulaEntriesAdded +
                            counts.formulaEntriesReplaced +
                            counts.formulaEntriesDeleted +
                            counts.sourceMaterialsAdded +
                            counts.sourceMaterialsReplaced +
                            counts.sourceMaterialsDeleted >
                            0
                            ? 'Content was rewritten, but the result is textually identical to the previous version — nothing visibly changed.'
                            : 'No changed items'
                    }
                />
            )
        })()
    ) : null

    return (
        <Drawer
            destroyOnClose
            footer={
                <div className={styles.footer}>
                    {job &&
                        (job.status === AgentContentJobStatus.failed ||
                            job.status === AgentContentJobStatus.canceled ||
                            job.status === AgentContentJobStatus.orphaned) && (
                            <Tooltip
                                title={
                                    job.sourceMaterialImport?.files.length
                                        ? "File-based imports can't be rerun automatically — re-upload the files listed above to run again"
                                        : undefined
                                }
                            >
                                <span>
                                    <Button
                                        loading={isRerunning}
                                        disabled={
                                            !!job.sourceMaterialImport?.files
                                                .length ||
                                            (props.isDisplayedCourseReadOnly &&
                                                job.courseName ===
                                                    props.displayedCourseName)
                                        }
                                        onClick={() => void rerunJob()}
                                    >
                                        Rerun with same request
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    {job &&
                        job.mode === AgentContentJobMode.courseWideEdit &&
                        !!job.courseWideEdit &&
                        job.courseWideEdit.sectionsApplied > 0 &&
                        !job.courseWideEdit.revertedAt &&
                        !isActive(job.status) && (
                            <Button
                                danger
                                disabled={
                                    props.isDisplayedCourseReadOnly &&
                                    job.courseName === props.displayedCourseName
                                }
                                loading={isReverting}
                                onClick={confirmRevertRun}
                            >
                                Revert entire run
                            </Button>
                        )}
                    {job && isActive(job.status) && (
                        <Button
                            danger
                            disabled={
                                props.stopping ||
                                (props.isDisplayedCourseReadOnly &&
                                    job.courseName ===
                                        props.displayedCourseName)
                            }
                            loading={props.stopping}
                            onClick={() => void props.onStop(job.id)}
                        >
                            Stop
                        </Button>
                    )}
                    {/* Course-wide edits have no single place to open */}
                    {navigationJob?.mode !==
                        AgentContentJobMode.courseWideEdit && (
                        <Button
                            disabled={!navigationJob}
                            onClick={() =>
                                navigationJob &&
                                void props.onOpenSection(navigationJob)
                            }
                        >
                            {navigationJob?.mode ===
                            AgentContentJobMode.formulaSheet
                                ? 'Open formula sheet'
                                : navigationJob?.mode ===
                                    AgentContentJobMode.previousExamAuthoring
                                  ? 'Open previous exams'
                                  : navigationJob?.mode ===
                                      AgentContentJobMode.sourceMaterialImport
                                    ? 'Open source materials'
                                    : navigationJob?.targetNotecardIds?.length
                                      ? 'Open notecard'
                                      : 'Open section'}
                        </Button>
                    )}
                </div>
            }
            onClose={props.onClose}
            open={!!props.jobId}
            placement={'right'}
            title={
                <div className={styles.header}>
                    <span className={styles.courseName}>
                        {metadata?.prettyCourseName ??
                            display?.prettyCourseName ??
                            job?.courseName ??
                            'Run'}
                    </span>
                    <span className={styles.breadcrumb}>
                        {metadata?.contentPathTitles.length
                            ? metadata.contentPathTitles.join(' › ')
                            : navigationJob &&
                                navigationJob.contentPath.length === 0
                              ? 'Entire course'
                              : navigationJob?.contentPathTitles.join(' › ') ??
                                '—'}
                    </span>
                    {job && (
                        <>
                            <Tag>{getAgentModelLabel(job.modelName)}</Tag>
                            <Tag color={statusColor(job.status)}>
                                {statusLabel(job.status)}
                            </Tag>
                        </>
                    )}
                </div>
            }
            width={'85%'}
        >
            {loading ? (
                <Spin />
            ) : (
                <Tabs
                    activeKey={activeTab}
                    items={[
                        {
                            children: overview,
                            key: 'overview',
                            label: 'Overview',
                        },
                        {
                            children: changes,
                            key: 'changes',
                            label: 'Changes',
                        },
                    ]}
                    onChange={(key) => {
                        setActiveTab(key)
                        // Shallow replace (no history entry): tab flips don't
                        // stack, but the CURRENT entry remembers the tab for
                        // when the user navigates away and comes Back
                        const nextQuery = { ...router.query }
                        if (key === 'changes') nextQuery.runTab = 'changes'
                        else delete nextQuery.runTab
                        void router.replace(
                            { pathname: router.pathname, query: nextQuery },
                            undefined,
                            { shallow: true }
                        )
                    }}
                />
            )}
        </Drawer>
    )
}
